import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from '@/stores/useSuiteStore'

// Miroir des réponses de boostcore `admin/cronicle` (CronicleAdminController).

export type CronicleEnv = 'PRD' | 'REC' | 'DEV' | 'OTHER'
export type CronicleEnvFilter = CronicleEnv | 'ALL'

export interface CronicleJobLastRun {
  jobId: string
  success: boolean
  code: number
  description: string | null
  startedAt: number | null // epoch secondes
  endedAt: number | null
  elapsedSec: number | null
}

export interface CronicleJob {
  id: string
  title: string
  enabled: boolean
  env: CronicleEnv
  categoryId: string | null
  plugin: string | null
  target: string | null
  timing: unknown | null
  running: boolean
  lastRun: CronicleJobLastRun | null
}

export interface CronicleHistoryRow {
  id: string
  event: string
  code: number
  description?: string
  time_start?: number
  time_end?: number
  elapsed?: number
}

export const useCronicleStore = defineStore('cronicle', () => {
  const suiteStore = useSuiteStore()

  const jobs = ref<CronicleJob[]>([])
  const isLoading = ref(false)
  const runningId = ref<string | null>(null)
  const error = ref('')

  async function fetchJobs(env: CronicleEnvFilter = 'ALL'): Promise<void> {
    isLoading.value = true
    error.value = ''
    try {
      const suffix = env && env !== 'ALL' ? `?env=${env}` : ''
      const res = await suiteStore.suiteFetch(`admin/cronicle/jobs${suffix}`, {
        method: 'GET',
        credentials: 'include',
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        jobs.value = ((await res.json()) as CronicleJob[]) ?? []
      } else {
        error.value = 'Impossible de charger les jobs Cronicle.'
      }
    } catch (e) {
      error.value = (e as Error).message
    } finally {
      isLoading.value = false
    }
  }

  async function runJob(id: string): Promise<{ ok: boolean; message?: string }> {
    runningId.value = id
    try {
      const res = await suiteStore.suiteFetch(`admin/cronicle/jobs/${encodeURIComponent(id)}/run`, {
        method: 'POST',
        credentials: 'include',
      })
      if (res && typeof res !== 'boolean' && res.ok) return { ok: true }
      let message = 'Échec du lancement'
      if (res && typeof res !== 'boolean') {
        try {
          const b = await res.json()
          if (b?.message) message = Array.isArray(b.message) ? b.message.join(', ') : String(b.message)
        } catch {
          // ignore
        }
      }
      return { ok: false, message }
    } catch (e) {
      return { ok: false, message: (e as Error).message }
    } finally {
      runningId.value = null
    }
  }

  async function fetchHistory(id: string, limit = 20): Promise<CronicleHistoryRow[]> {
    const res = await suiteStore.suiteFetch(
      `admin/cronicle/jobs/${encodeURIComponent(id)}/history?limit=${limit}`,
      { method: 'GET', credentials: 'include' },
    )
    if (res && typeof res !== 'boolean' && res.ok) {
      return ((await res.json()) as CronicleHistoryRow[]) ?? []
    }
    return []
  }

  return { jobs, isLoading, runningId, error, fetchJobs, runJob, fetchHistory }
})
